/*
 * File: ClinicalList.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Creates and styles that list of Clinical Widgets
 */
import React from "react";
import {
  List,
  ListItem,
  Divider,
  withStyles,
  Grid,
  Typography,
  Theme,
  createStyles
} from "@material-ui/core";
import ClinicalStore from "./Store";
import UINotFound from "components/ui/UINotFound";
import { observer } from "mobx-react";
import PatientIdStorage from "../store/PatientIdStore";
import { usePreference, useStore } from "utils/useStore";
import { WithStyles } from "@material-ui/styles";
import { Instance } from "mobx-state-tree";
import { Link } from "react-router-dom";
import { ClinicalPreferenceValue } from "services/preferences/models/ClinicalPreferenceValue";
import { GridDirection } from "@material-ui/core";

const styles = (theme: Theme) =>
  createStyles({
    listItem: {
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
      textAlign: "center"
    },
    list: {
      margin: theme.spacing(0, 2)
    },
    listItemTitle: {
      textAlign: "center",
      width: "100%",
      color: theme.palette.common.black,
      fontWeight: 500
    },
    divider: {
      background: "transparent",
      height: theme.spacing(2)
    },
    fullWidth: {
      width: "100%"
    },
    gridItem: {
      maxWidth: "100%"
    }
  });

interface ItemProps extends WithStyles<typeof styles> {
  pref: Instance<typeof ClinicalPreferenceValue>;
  count: number;
  patientId: number;
}

const ClinicalListItem = observer(
  ({ pref, classes, count, patientId }: ItemProps) => {
    const linkTo = `/patients/${patientId}/clinical/` + pref.key;
    return pref.showInView ? (
      <React.Fragment>
        <Link to={linkTo}>
          <ListItem
            className={classes.listItem}
            style={{ backgroundColor: "#" + pref.color }}
          >
            <Typography variant="h6" className={classes.listItemTitle}>
              {pref.title} {count > 0 ? "(" + count + ")" : ""}
            </Typography>
          </ListItem>
        </Link>
        <Divider className={classes.divider} />
      </React.Fragment>
    ) : null;
  }
);

type Props = WithStyles<typeof styles>;

const ClinicalList = ({ classes }: Props) => {
  const patientId = PatientIdStorage.getId();
  const clinical = useStore(ClinicalStore);
  let widgets: Instance<typeof ClinicalPreferenceValue>[] = usePreference(
    "clinicalSummary"
  ).sortedPreferences;
  if (!patientId) return <UINotFound message="No patient selected" />;

  const keyword = clinical.keyword;
  if (keyword.length > 0) {
    widgets = widgets.filter(w => {
      return w.title.toLowerCase().includes(keyword) && w.showInView;
    });
  }

  let layoutDirection: GridDirection = "row";
  const firstSection = widgets.filter(w => w.columnNumber === 0);
  const secondSection = widgets.filter(w => w.columnNumber === 1);
  if (firstSection.length === 0 || secondSection.length === 0)
    layoutDirection = "column";

  //Remove this line when we did implement api endpoints for the below clinical widgets in vapor server.  
  const missingPrefApiKeys = ["hieDownloads", "immunizationScheduling", "opioidTreatmentAgreement", "healthConcerns"];

  return (
    <Grid
      container
      direction={layoutDirection}
      justify="center"
      alignItems="stretch"
    >
      {[firstSection, secondSection].map((prefs, columnId) => {
        return (
          <Grid item xs={12} sm={6} key={columnId} className={classes.gridItem}>
            <List className={classes.list}>
              {prefs.map((pref, idx) => {
                const clinicalInfo = clinical.counts.get(pref.key);
                if (clinicalInfo === undefined) {
                  if (!missingPrefApiKeys.includes(pref.key)) {
                    throw new Error(`Missing clinical count for clinical widget "${pref.key}"`);
                  }
                  return  null
                }
                return (
                  <ClinicalListItem
                    key={idx}
                    pref={pref}
                    classes={classes}
                    patientId={patientId}
                    count={clinicalInfo.count}
                  />
                );
              })}
            </List>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default withStyles(styles)(observer(ClinicalList));
